/**
 * Persistent active-session marker. Stored in localStorage so the player can
 * lock their iPad, press home, refresh the page, or accidentally close the
 * tab — and when they come back, the app drops them straight into the same
 * lesson (single-player) or asks them to rejoin the battle room (multiplayer).
 *
 * We only persist enough to identify WHERE the player was. The actual run
 * state (distance, hearts, etc.) starts fresh on resume — that's the
 * intentional tradeoff: we can't replay obstacle physics, but we can take
 * them right back to the lesson so they don't lose 30+ seconds of navigating.
 *
 * Sessions older than 30 minutes are ignored — assume the player meant to
 * stop and we shouldn't surprise them with a resume.
 */

// v2: changed semantics — battle marker now only set while actually racing,
// not while in lobby. Old v1 markers are ignored on next load.
const STORAGE_KEY = "wr_active_session_v2";
const STALE_MS = 30 * 60 * 1000; // 30 minutes

export interface ActiveSession {
  /** When this session was last touched. */
  updatedAt: number;
  /** Solo: lesson id the player was running. */
  soloLessonId?: string;
  /** Multiplayer: room code + role. */
  battle?: {
    code: string;
    asHost: boolean;
  };
}

function read(): ActiveSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveSession;
    if (!parsed.updatedAt || Date.now() - parsed.updatedAt > STALE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(s: ActiveSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...s, updatedAt: Date.now() }));
  } catch { /* quota / private — ignore */ }
}

export function getActiveSession(): ActiveSession | null {
  return read();
}

export function setSoloSession(lessonId: string): void {
  write({ updatedAt: Date.now(), soloLessonId: lessonId });
}

export function setBattleSession(code: string, asHost: boolean): void {
  write({ updatedAt: Date.now(), battle: { code, asHost } });
}

export function touchSession(): void {
  const s = read();
  if (s) write(s);
}

export function clearSession(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
